/**
 * MvbDrillView (P370) -- the MVB validation drill (gap-analysis F6).
 * Route: /twin/mvb-drill.
 *
 * The rehearsal that turns a declared minimum viable bank into a
 * proven one: the recovery sequence executed against total loss, with
 * actual recorded against projected per step, and the variance fed
 * back into the rated model.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { drillStatusLabel } from "shared/vocab/workflowState";

import { api, type ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";

interface DrillStep {
  step_id: string;
  step_index: number;
  wave: number;
  names: string;
  starts_at_hours: number;
  projected_finish_hours: number;
  actual_finish_hours: number | null;
  variance_hours: number | null;
  notes: string | null;
}

interface DrillSummary {
  projected_total_hours: number;
  actual_total_hours: number | null;
  variance_hours: number | null;
  steps_recorded: number;
  steps_missing_actuals: number[];
  worst_step_variance_hours: number | null;
  horizons_actual: {
    horizon: string;
    deadline_hours: number;
    members: number;
    actual_hours: number;
    met: boolean;
  }[];
}

interface Drill {
  id: string;
  name: string;
  scheduled_for: string;
  status: string;
  summary: DrillSummary | null;
  steps: DrillStep[];
}

const HORIZON_LABEL: Record<string, string> = {
  t_plus_4h: "T+4h",
  t_plus_24h: "T+24h",
  t_plus_72h: "T+72h",
  t_plus_1w: "T+1w",
};

function varianceTone(v: number | null): string {
  if (v === null) return "text-slate-400";
  if (v > 0) return "text-rose-700";
  if (v < 0) return "text-emerald-700";
  return "text-slate-500";
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MvbDrillView() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useMe();
  // Every write here is admin-gated at the endpoint. Showing the
  // ceremony to somebody who will be refused it is the offer-an-action-
  // the-seat-cannot-complete defect this programme spent four rounds
  // removing.
  const isAdmin = me.data?.is_admin ?? false;
  const [openId, setOpenId] = useState<string | null>(null);

  // Round 10 F1 -- the recovery sequence sends a reader here to "test
  // it" and this screen could only LIST drills: no way to schedule
  // one, start it, record what actually happened or close it. The
  // endpoints for all four have existed since P370; the ceremony was
  // simply never surfaced, so the pointer from the projection led to a
  // page that could not perform the test it names.
  const [scheduling, setScheduling] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftDate, setDraftDate] = useState(today());
  const [actuals, setActuals] = useState<Record<string, string>>({});

  const failed = (e: unknown) =>
    toast.push({
      tone: "error",
      message: (e as ApiError)?.message ?? t("mvbDrill.failed"),
    });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["twin", "mvb-drills"] });
    void qc.invalidateQueries({ queryKey: ["twin", "mvb-drill"] });
  };

  const createDrill = useMutation({
    mutationFn: (body: { name: string; scheduled_for: string }) =>
      api.post<Drill>("/v1/admin/twin/mvb/drills", body),
    onSuccess: (d) => {
      // The plan is frozen at creation, so the drill that opens is the
      // one the reader was just looking at rather than whatever the
      // model says next week.
      setOpenId(d.id);
      setScheduling(false);
      setDraftName("");
      toast.push({
        tone: "success",
        message: t("mvbDrill.scheduled", { name: d.name }),
      });
      refresh();
    },
    onError: failed,
  });

  const startDrill = useMutation({
    mutationFn: (id: string) =>
      api.post<Drill>(`/v1/admin/twin/mvb/drills/${id}/start`, {}),
    onSuccess: () => {
      toast.push({ tone: "success", message: t("mvbDrill.started") });
      refresh();
    },
    onError: failed,
  });

  const recordStep = useMutation({
    mutationFn: (v: { drillId: string; stepId: string; hours: number }) =>
      api.patch<Drill>(`/v1/admin/twin/mvb/drills/${v.drillId}/steps/${v.stepId}`, {
        actual_finish_hours: v.hours,
      }),
    onSuccess: () => {
      toast.push({ tone: "success", message: t("mvbDrill.recorded") });
      refresh();
    },
    onError: failed,
  });

  const completeDrill = useMutation({
    mutationFn: (id: string) =>
      api.post<Drill>(`/v1/admin/twin/mvb/drills/${id}/complete`, {}),
    onSuccess: () => {
      toast.push({ tone: "success", message: t("mvbDrill.completed") });
      refresh();
    },
    onError: failed,
  });

  const list = useQuery({
    queryKey: ["twin", "mvb-drills"],
    queryFn: () => api.get<{ rows: Drill[] }>("/v1/twin/mvb/drills"),
  });
  const detail = useQuery({
    queryKey: ["twin", "mvb-drill", openId],
    queryFn: () => api.get<Drill>(`/v1/twin/mvb/drills/${openId}`),
    enabled: Boolean(openId),
  });

  const applyActuals = useMutation({
    mutationFn: (id: string) =>
      api.post<{ applied: { service: string }[]; skipped_blocks: string[] }>(
        `/v1/admin/twin/mvb/drills/${id}/apply-actuals`,
        {},
      ),
    onSuccess: (r) => {
      toast.push({
        tone: "success",
        message: t("mvbDrill.applied", {
          count: r.applied.length,
          skipped: r.skipped_blocks.length,
        }),
      });
      void qc.invalidateQueries({ queryKey: ["twin"] });
    },
  });

  const d = detail.data;

  return (
    <article data-testid="mvb-drill-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="drill" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("mvbDrill.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("mvbDrill.body")}</p>
      </header>

      {isAdmin && (
        <section data-testid="mvb-drill-schedule" className="space-y-2">
          {!scheduling && (
            <button
              type="button"
              data-testid="mvb-drill-schedule-open"
              onClick={() => {
                setDraftName(t("mvbDrill.defaultName", { date: today() }));
                setScheduling(true);
              }}
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-semibold text-brand-brassDark hover:bg-brand-brass/10"
            >
              {t("mvbDrill.schedule")}
            </button>
          )}
          {scheduling && (
            <div className="flex flex-wrap items-end gap-2 rounded border border-paper-2 bg-white p-3">
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                {t("mvbDrill.nameLabel")}
                <input
                  type="text"
                  data-testid="mvb-drill-name"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  className="w-64 rounded border border-paper-2 px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                {t("mvbDrill.dateLabel")}
                <input
                  type="date"
                  data-testid="mvb-drill-date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  className="rounded border border-paper-2 px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                data-testid="mvb-drill-schedule-confirm"
                disabled={!draftName.trim() || createDrill.isPending}
                onClick={() =>
                  createDrill.mutate({
                    name: draftName.trim(),
                    scheduled_for: draftDate,
                  })
                }
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
              >
                {createDrill.isPending
                  ? t("mvbDrill.scheduling")
                  : t("mvbDrill.scheduleConfirm")}
              </button>
              <button
                type="button"
                onClick={() => setScheduling(false)}
                className="rounded border border-paper-2 px-3 py-1 text-xs text-slate-700"
              >
                {t("mvbDrill.cancel")}
              </button>
              <p className="w-full text-xs text-slate-500">
                {t("mvbDrill.scheduleNote")}
              </p>
            </div>
          )}
        </section>
      )}

      {/* Round 19 Q6 -- a page that offered nothing to schedule and said
          nothing about why read as a dead end. */}
      {!isAdmin && me.data && (
        <p data-testid="mvb-drill-schedule-why" className="text-xs text-slate-500">
          {t("mvbDrill.scheduleWhyNot")}
        </p>
      )}

      <section data-testid="mvb-drill-list" className="space-y-1">
        {(list.data?.rows ?? []).length === 0 && (
          <p className="text-sm text-slate-500">{t("mvbDrill.empty")}</p>
        )}
        {(list.data?.rows ?? []).map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => setOpenId(row.id === openId ? null : row.id)}
            className="flex w-full flex-wrap items-baseline gap-x-3 rounded border border-paper-2 bg-white px-3 py-2 text-start text-sm hover:bg-paper"
          >
            <span className="font-medium text-slate-900">{row.name}</span>
            <span className="font-mono text-xs text-slate-500">
              {row.scheduled_for}
            </span>
            <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-600">
              {drillStatusLabel(row.status)}
            </span>
            {row.summary?.actual_total_hours != null && (
              <span className="font-mono text-xs tabular-nums text-slate-600">
                {t("mvbDrill.projectedVsActual", {
                  projected: row.summary.projected_total_hours,
                  actual: row.summary.actual_total_hours,
                })}
              </span>
            )}
          </button>
        ))}
      </section>

      {d && (
        <section data-testid="mvb-drill-detail" className="space-y-3">
          {isAdmin && (d.status === "planned" || d.status === "in_progress") && (
            <div data-testid="mvb-drill-actions" className="flex flex-wrap gap-2">
              {d.status === "planned" && (
                <button
                  type="button"
                  data-testid="mvb-drill-start"
                  disabled={startDrill.isPending}
                  onClick={() => startDrill.mutate(d.id)}
                  className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
                >
                  {t("mvbDrill.start")}
                </button>
              )}
              <button
                type="button"
                data-testid="mvb-drill-complete"
                disabled={completeDrill.isPending}
                onClick={() => completeDrill.mutate(d.id)}
                className="rounded border border-slate-400 bg-slate-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
              >
                {t("mvbDrill.complete")}
              </button>
              {/* Closing on a step with no actual is allowed -- a real
                  rehearsal gets abandoned -- but the verdict is then
                  computed over what was observed, so say which steps
                  are still blank rather than letting the summary imply
                  they were all run. */}
              <span className="self-center text-xs text-slate-500">
                {t("mvbDrill.recordedCount", {
                  recorded: d.steps.filter((x) => x.actual_finish_hours !== null)
                    .length,
                  total: d.steps.length,
                })}
              </span>
            </div>
          )}
          {d.summary && (
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded border border-paper-2 bg-white px-2 py-1">
                {t("mvbDrill.projectedVsActual", {
                  projected: d.summary.projected_total_hours,
                  actual: d.summary.actual_total_hours ?? "—",
                })}
              </span>
              {d.summary.horizons_actual.map((h) => (
                <span
                  key={h.horizon}
                  className={`rounded px-2 py-1 ${
                    h.met
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-rose-100 text-rose-800"
                  }`}
                >
                  {HORIZON_LABEL[h.horizon] ?? h.horizon}{" "}
                  {h.met ? t("mvbDrill.met") : t("mvbDrill.missed")}
                </span>
              ))}
              {d.status === "completed" && (
                <button
                  type="button"
                  data-testid="mvb-drill-apply"
                  onClick={() => applyActuals.mutate(d.id)}
                  disabled={applyActuals.isPending}
                  className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10"
                >
                  {t("mvbDrill.apply")}
                </button>
              )}
            </div>
          )}
          <table
            data-testid="mvb-drill-steps"
            className="w-full border-collapse text-sm"
          >
            <thead>
              <tr className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-1 text-start">{t("mvbDrill.step")}</th>
                <th className="text-end">{t("mvbDrill.projected")}</th>
                <th className="text-end">{t("mvbDrill.actual")}</th>
                <th className="text-end">{t("mvbDrill.variance")}</th>
              </tr>
            </thead>
            <tbody>
              {d.steps.map((s) => (
                <tr key={s.step_id} className="border-t border-paper-2">
                  <td className="py-1">
                    <span className="font-mono text-xs text-slate-400">
                      {t("screens.mvbDrillView.w")}
                      {s.wave}
                    </span>{" "}
                    {s.names}
                  </td>
                  <td className="text-end font-mono tabular-nums">
                    {t("screens.mvbDrillView.t")}
                    {s.projected_finish_hours}
                    {t("screens.mvbDrillView.h")}
                  </td>
                  <td className="text-end font-mono tabular-nums">
                    {isAdmin &&
                    (d.status === "planned" || d.status === "in_progress") ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          aria-label={t("mvbDrill.actualFor", { step: s.names })}
                          data-testid={`mvb-drill-actual-${s.step_id}`}
                          value={
                            actuals[s.step_id] ??
                            (s.actual_finish_hours !== null
                              ? String(s.actual_finish_hours)
                              : "")
                          }
                          onChange={(e) =>
                            setActuals((prev) => ({
                              ...prev,
                              [s.step_id]: e.target.value,
                            }))
                          }
                          className="w-20 rounded border border-paper-2 px-1 py-0.5 text-end text-xs"
                        />
                        <button
                          type="button"
                          data-testid={`mvb-drill-record-${s.step_id}`}
                          disabled={
                            recordStep.isPending ||
                            !/^\d+$/.test(
                              (
                                actuals[s.step_id] ??
                                (s.actual_finish_hours !== null
                                  ? String(s.actual_finish_hours)
                                  : "")
                              ).trim(),
                            )
                          }
                          onClick={() =>
                            recordStep.mutate({
                              drillId: d.id,
                              stepId: s.step_id,
                              hours: Number(
                                actuals[s.step_id] ?? s.actual_finish_hours ?? 0,
                              ),
                            })
                          }
                          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-1.5 py-0.5 text-[11px] text-brand-brassDark disabled:opacity-40"
                        >
                          {t("mvbDrill.record")}
                        </button>
                      </span>
                    ) : s.actual_finish_hours !== null ? (
                      `T+${s.actual_finish_hours}h`
                    ) : (
                      "—"
                    )}
                  </td>
                  <td
                    className={`text-end font-mono tabular-nums ${varianceTone(
                      s.variance_hours,
                    )}`}
                  >
                    {s.variance_hours !== null
                      ? `${s.variance_hours > 0 ? "+" : ""}${s.variance_hours}h`
                      : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500">{t("mvbDrill.note")}</p>
        </section>
      )}
    </article>
  );
}
